// src/pages/Homepage.js

import { Helmet } from 'react-helmet-async';
import { useEffect, useRef, useState } from 'react'; 
import { Link } from 'react-router-dom'; 
import BookCarousel from '../components/BookCarousel';
import CreateBookSection from '../components/CreateBookSection'; 
import PartnersSection from '../components/PartnerSection';
import SubscriptionSection from '../components/SubscriptionSection';
import EventsSection from '../components/EventsSection';
import { useTranslation } from 'react-i18next';

const Homepage = () => {
  const { t } = useTranslation();
  const [isVideoLoaded, setIsVideoLoaded] = useState(false); 
  
  const sectionRefs = useRef([]);
  const scrollRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('cb-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const refs = sectionRefs.current;
    refs.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      refs.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const handleStart = (e) => {
      isDragging.current = true;
      startX.current = (e.pageX || e.touches[0].pageX) - element.offsetLeft;
      scrollLeft.current = element.scrollLeft;
      element.style.cursor = 'grabbing';
    };

    const handleMove = (e) => {
      if (!isDragging.current) return;
      e.preventDefault();
      const x = (e.pageX || e.touches[0].pageX) - element.offsetLeft;
      const walk = (x - startX.current) * 1.5;
      element.scrollLeft = scrollLeft.current - walk;
    };

    const handleEnd = () => {
      isDragging.current = false;
      element.style.cursor = 'grab';
    };

    element.addEventListener('mousedown', handleStart);
    element.addEventListener('mousemove', handleMove);
    element.addEventListener('mouseup', handleEnd);
    element.addEventListener('mouseleave', handleEnd);
    element.addEventListener('touchstart', handleStart);
    element.addEventListener('touchmove', handleMove);
    element.addEventListener('touchend', handleEnd);

    return () => {
      element.removeEventListener('mousedown', handleStart);
      element.removeEventListener('mousemove', handleMove);
      element.removeEventListener('mouseup', handleEnd);
      element.removeEventListener('mouseleave', handleEnd);
      element.removeEventListener('touchstart', handleStart);
      element.removeEventListener('touchmove', handleMove);
      element.removeEventListener('touchend', handleEnd);
    };
  }, []);

  const addToRefs = (el) => {
    if (el && !sectionRefs.current.includes(el)) {
      sectionRefs.current.push(el);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('homePage.meta.title')}</title>
        <link rel="canonical" href="https://studio.littlehibba.com" />
        <meta name="description" content={t('homePage.meta.description')} />
        <meta property="og:title" content={t('homePage.meta.title')} />
        <meta property="og:description" content={t('homePage.meta.description')} />
      </Helmet>

      {/* Hero Section */}
      <section className="video-section position-relative">
        <video 
          autoPlay 
          loop 
          muted 
          playsInline 
          poster="/videos/herovideo-poster.jpg"
          onCanPlay={() => setIsVideoLoaded(true)}
          className={`hero-video ${isVideoLoaded ? 'loaded' : ''}`}
          preload="metadata" 
        >
          <source src="/videos/herovideo.webm" type="video/webm" />
          <source src="/videos/herovideo.mp4" type="video/mp4" />
          {t('homePage.videoNotSupported')}
        </video>
        <div className="hero-text-overlay">
          <span>{t('homePage.hero.line1')}</span>
          <span>{t('homePage.hero.line2')}</span>
        </div>
      </section>
      
      {/* Etkinliklerin sergilendiği bileşen. Linkleme mantığı bu dosyanın içinde yapılmalı. */}
      <EventsSection />

      {/* --- MEET SAJEDA BÖLÜMÜ --- */}
      <section className="pt-5 pb-2" style={{ backgroundColor: '#f8f9fa' }} ref={addToRefs}>
        <div className="container pt-4 pb-2">
          <div className="row align-items-center bg-white rounded-5 shadow-sm overflow-hidden">
            {/* Sol Taraf: Kare Formatında Görsel */}
            <div className="col-lg-5 p-0">
              <img 
                src="/images/sajeda/12.jpg" 
                alt="Sajeda - Founder of Little Hibba" 
                className="img-fluid w-100"
                style={{ objectFit: 'cover', aspectRatio: '1/1' }}
              />
            </div>
            
            {/* Sağ Taraf: Metin ve Buton */}
            <div className="col-lg-7 p-4 p-md-5">
              <span className="badge bg-light text-primary border border-primary text-uppercase mb-3 px-3 py-2" style={{ letterSpacing: '1px' }}>
                Meet The Founder
              </span>
              <h2 className="display-5 fw-bold mb-3" style={{ fontFamily: "'Playfair Display', serif", color: "#1a365d" }}>
                Salam! I'm Sajeda.
              </h2>
              <p className="lead text-muted mb-4" style={{ lineHeight: '1.8' }}>
                I’m the founder of Little Hibba. My goal is to make the publishing industry more accessible to children's book creators by sharing knowledge, practical guidance, and opportunities, while fostering a thriving community of Muslim authors who support and inspire one another. 
              </p>
              <Link to="/publishing-program" className="btn btn-outline-dark btn-lg rounded-pill px-5 fw-bold shadow-sm transition-all">
                Work with me
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* --- BRITISH BOOK AWARDS FINALIST SECTION --- */}
      <section className="pt-2 pb-5" style={{ backgroundColor: '#f8f9fa' }} ref={addToRefs}>
        <div className="container text-center pt-2 pb-4">
          <span className="text-uppercase fw-bold d-block mb-3" style={{ fontSize: '0.75rem', letterSpacing: '2.5px', color: '#64748b' }}>
            Small Press of the Year Finalist
          </span>
          <div className="row justify-content-center">
            <div className="col-auto">
              <img 
                src="/images/the-british-book-awards-dark-12001.png" 
                alt="The British Book Awards 2026 - Small Press of the Year Finalist" 
                className="img-fluid"
                style={{ maxHeight: '85px', objectFit: 'contain' }}
              />
            </div>
          </div>
        </div>
      </section>
      {/* --------------------------------------- */}
      
      <CreateBookSection t={t} addToRefs={addToRefs} />
      <BookCarousel addToRefs={addToRefs} />
      <PartnersSection />
      <SubscriptionSection t={t} addToRefs={addToRefs} />
         
    </>
  );
};

export default Homepage;